// src/pages/Payment.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").trim();

export default function Payment() {
  const navigate = useNavigate();
  const [planId, setPlanId] = useState(null); // current user's plan id
  const [plans, setPlans] = useState([]); // all plans with features

  const handleBack = () => {
    navigate(-1);
  };

  // 🔐 Fetch current planId
  useEffect(() => {
    const fetchCurrentPlan = async () => {
      try {
        const token = localStorage.getItem("jwt_token");
        if (!token) {
          alert("⚠️ No token found. Please log in.");
          return;
        }

        const response = await fetch(`${BACKEND_URL}/api/payments/current-plan`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await response.json();
        if (data.plan_id) {
          setPlanId(data.plan_id);
        } else {
          console.warn("⚠️ No plan_id returned");
        }
      } catch (err) {
        console.error("Error fetching current plan:", err);
      }
    };

    fetchCurrentPlan();
  }, []);

  // 📦 Fetch available plans (with features) from backend
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/payments/plans`);
        if (!res.ok) throw new Error("Failed to fetch plans");
        const data = await res.json();
        setPlans(data);
      } catch (err) {
        console.error("⚠️ Error fetching plans:", err);
      }
    };

    fetchPlans();
  }, []);

  // 🔑 Start payment via Cashfree
  const startPayment = async (planId, amount) => {
    try {
      const user_id = localStorage.getItem("user_id");
      if (!user_id) {
        alert("⚠️ User not logged in. Please log in first.");
        return;
      }

      const response = await fetch(
        `${BACKEND_URL}/api/payments/create-order?planId=${planId}&amount=${amount}&user_id=${user_id}`,
        { method: "GET" }
      );

      const data = await response.json();
      console.log("🎟️ Payment session:", data.payment_session_id);

      if (!data.payment_session_id) {
        alert("❌ Failed to get payment session ID");
        return;
      }

      if (!window.Cashfree) {
        console.error("❌ Cashfree SDK v3 not loaded");
        alert("Cashfree SDK failed to load.");
        return;
      }

      const cashfree = window.Cashfree({ mode: "production" }); // change to sandbox for testing
      cashfree.checkout({
        paymentSessionId: data.payment_session_id,
        redirectTarget: "_self",
      });
    } catch (err) {
      console.error("⚠️ Error while starting payment:", err);
      alert("⚠️ Payment initiation failed");
    }
  };

  // 📌 Render plan card
  const renderPlan = (plan) => {
    if (plan.plan_name.toUpperCase() === "CUSTOM") {
      // Special case: custom plan
      return (
        <div
          className="bg-[#173a32] border border-white/10 rounded-2xl shadow-2xl shadow-black/20 p-8 flex flex-col justify-between max-w-sm w-full hover:border-emerald-400/50 transition"
          key={plan.plan_id}
        >
          <div className="mb-6 inline-flex items-center justify-center w-full">
            <div className="bg-gradient-to-r from-amber-500 to-red-500 px-4 py-2 rounded-full">
              <h2 className="text-white text-lg font-bold">{plan.plan_name}</h2>
            </div>
          </div>
          <div>
            <p className="text-lg font-semibold text-emerald-200 mb-4 text-center">
              For visitors &gt; {plan.visitors_allowed.toLocaleString()}/month
            </p>
            <p className="mb-8 text-slate-300 text-center text-sm leading-6">
              Need a custom plan? Contact our sales team at{" "}
              <a
                href="mailto:madhavan@nokose.com"
                className="text-emerald-400 font-semibold hover:text-emerald-300 transition"
              >
                madhavan@nokose.com
              </a>
            </p>
          </div>
          <button
            type="button"
            className="w-full bg-gradient-to-r from-amber-500 to-red-500 hover:from-amber-600 hover:to-red-600 text-white font-bold py-3 rounded-lg transition"
          >
            Get Custom Quote
          </button>
        </div>
      );
    }

    // Normal plan (Growth, Pro, etc.)
    const isPopular = plan.plan_name.toUpperCase() === "PRO";
    return (
      <div
        className={`bg-[#173a32] rounded-2xl shadow-2xl shadow-black/20 p-8 flex flex-col justify-between max-w-sm w-full transition ${
          isPopular 
            ? "border-2 border-emerald-400/70 ring-2 ring-emerald-400/20" 
            : "border border-white/10 hover:border-emerald-400/50"
        }`}
        key={plan.plan_id}
      >
        {isPopular && (
          <div className="mb-4 inline-flex items-center justify-center w-full">
            <span className="bg-emerald-500/20 text-emerald-200 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
              Most Popular
            </span>
          </div>
        )}
        
        <div className={`mb-6 inline-flex items-center justify-center w-full ${isPopular ? "" : ""}`}>
          <div className={`px-4 py-2 rounded-full ${
            isPopular 
              ? "bg-gradient-to-r from-emerald-400 to-emerald-500" 
              : "bg-white/10 border border-white/20"
          }`}>
            <h2 className={`text-lg font-bold ${isPopular ? "text-[#0f2b24]" : "text-emerald-200"}`}>
              {plan.plan_name}
            </h2>
          </div>
        </div>

        <div className="mb-6">
          <div className="text-3xl font-black text-emerald-200 text-center mb-2">
            ₹{plan.plan_price}
          </div>
          <p className="text-sm text-slate-300 text-center">/ month</p>
        </div>

        <div>
          <p className="text-base font-semibold text-slate-200 mb-4 text-center">
            Up to {plan.visitors_allowed.toLocaleString()} visitors/month
          </p>
          {plan.features?.length > 0 && (
            <ul className="text-sm text-slate-300 mb-8 space-y-3">
              {plan.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-emerald-400 font-bold text-lg flex-shrink-0">✓</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <button
          type="button"
          onClick={() => startPayment(plan.plan_id, plan.plan_price)}
          className={`w-full font-bold py-3 rounded-lg transition ${
            isPopular
              ? "bg-[#8ff0b3] text-[#0f2b24] hover:bg-[#b3f7ca]"
              : "bg-white/10 border border-emerald-400/50 text-emerald-200 hover:bg-white/20 hover:border-emerald-300"
          }`}
        >
          Upgrade to {plan.plan_name}
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#f3f4f6] text-slate-900 px-6 py-12">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(67,179,126,0.08),_transparent_40%),radial-gradient(circle_at_top_right,_rgba(59,130,246,0.05),_transparent_35%)] pointer-events-none" />
      
      <div className="relative">
        <header className="max-w-6xl mx-auto mb-16 flex justify-center items-start gap-6">
          <div className="text-center flex-1">
            <h1 className="text-4xl font-black text-slate-900 mb-2">💳 Plans & Pricing</h1>
            <p className="text-xl text-slate-600">Upgrade your plan to unlock more features and scale your experiments</p>
          </div>
          <button 
            onClick={handleBack} 
            className="bg-slate-200 hover:bg-slate-300 border border-slate-300 text-slate-900 px-4 py-2 rounded-lg font-semibold transition flex-shrink-0"
            aria-label="Go back"
          >
            ← Back
          </button>
        </header>

        <main className="max-w-7xl mx-auto">
          {plans.filter((p) => planId && p.plan_id > planId).length === 0 ? (
            <div className="text-center py-16">
              <p className="text-lg text-slate-700 mb-4">You already have the highest plan!</p>
              <p className="text-slate-600">Contact us to discuss custom solutions.</p>
            </div>
          ) : (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 justify-items-center">
              {plans
                .filter((p) => planId && p.plan_id > planId)
                .map((plan) => renderPlan(plan))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
